/**
 * MeReport FastAPI request view — mirrors FastAPIRequestView pattern
 * but points at config.ME_REPORT_FASTAPI host.
 *
 * - Auto-fetches Bearer permit via GeneratePermitAPI (using Django apiToken cookie)
 * - Shares the same `bearer` cookie with FastAPIRequestView (single permit per session)
 * - Auto-refreshes permit when backend returns "Permit"-related detail
 * - For blob responses (CSV/Excel), pass `extra: { responseType: "blob" }`
 *   and use `downloadFile()` from `react-lib/utils` on the returned network response.
 */

import { to } from "react-lib/utils";
import BaseService from "react-lib/apis/services/BaseService";
import GeneratePermitAPI from "./GeneratePermitAPI_users";
import Cookies from "js-cookie";
import config from "../../config/config";

const HOST = `${config.ME_REPORT_FASTAPI}`;

type RequestParams = {
  endpoint: string;
  params?: Record<string, any>;
  bearer?: string;
  extra?: Record<string, any>;
};

type RequestWithDataParams<D = any> = RequestParams & { data?: D };

type ApiResponse<T> = [T | null, any, any];

const refreshPermit = async (): Promise<string> => {
  const [result] = await GeneratePermitAPI.get({
    apiToken: Cookies.get("apiToken"),
  });

  const bearer: string = result?.permit || "";

  Cookies.set("bearer", bearer, { path: "/" });

  return bearer;
};

const setContentType = (base: BaseService, data: any) => {
  if (data instanceof URLSearchParams) {
    base.client.defaults.headers.common["Content-Type"] = "application/x-www-form-urlencoded";
  } else if (data instanceof FormData) {
    base.client.defaults.headers.common["Content-Type"] = "multipart/form-data";
  }
};

const isPermitError = (data: any): boolean =>
  typeof data?.detail === "string" && data.detail.includes("Permit");

const MeReportFastApiView = {
  get: async <T = any>(params: RequestParams): Promise<ApiResponse<T>> => {
    let bearer = params.bearer || Cookies.get("bearer");
    if (!bearer) {
      bearer = await refreshPermit();
    }

    const fetch = async (b: string) => {
      const base = new BaseService();
      base.client.defaults.headers.common["Authorization"] = `Bearer ${b}`;

      return await to(
        base.client
          .get(`${HOST}${params.endpoint}`, {
            params: params.params ? { ...params.params } : {},
            ...params.extra,
          })
          .then(base.handleResponse)
          .catch(base.throwErrorMessage),
      );
    };

    let result = await fetch(bearer);
    if (isPermitError(result[0])) {
      bearer = await refreshPermit();
      result = await fetch(bearer);
    }

    return [result[1], result[0], result[2]];
  },

  post: async <T = any, D = any>(params: RequestWithDataParams<D>): Promise<ApiResponse<T>> => {
    let bearer = params.bearer || Cookies.get("bearer");
    if (!bearer) {
      bearer = await refreshPermit();
    }

    const fetch = async (b: string) => {
      const base = new BaseService();
      base.client.defaults.headers.common["Authorization"] = `Bearer ${b}`;
      setContentType(base, params.data);

      return await to(
        base.client
          .post(`${HOST}${params.endpoint}`, params.data, {
            params: params.params ? { ...params.params } : {},
            ...params.extra,
          })
          .then(base.handleResponse)
          .catch(base.throwErrorMessage),
      );
    };

    let result = await fetch(bearer);
    if (isPermitError(result[0])) {
      bearer = await refreshPermit();
      result = await fetch(bearer);
    }

    return [result[1], result[0], result[2]];
  },
};

export default MeReportFastApiView;
